import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  FaArrowDown, FaArrowUp, FaBriefcase, FaCheckSquare, FaChevronDown, FaChevronUp, FaClipboardList, FaFileExcel, FaFilePdf,
  FaFilter, FaInfoCircle, FaSearch, FaSquare, FaUndo,
} from 'react-icons/fa';
import api from '../../api';
import YoungApprenticePeriodReport from './YoungApprenticePeriodReport';
import './YoungApprenticeReportTabs.css';
import YoungApprenticeAttendanceDialog from './YoungApprenticeAttendanceDialog';
import YoungApprenticeService from '../../services/YoungApprenticeService';
import { downloadYoungApprenticeTablePdf } from './youngApprenticeReportPdf';
import { downloadYoungApprenticeAttendancePdf } from './youngApprenticeAttendancePdf';
import YoungApprenticeProgramReport from './YoungApprenticeProgramReport';
import './YoungApprentice.css';
import './YoungApprenticeEnhancements.css';
import './YoungApprenticeOperational.css';
import '../../components/ReportColumnOrder.css';

const money = (cents) => cents == null ? '—' : (Number(cents || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dateBr = (value) => value ? String(value).slice(0, 10).split('-').reverse().join('/') : '—';
const yesNo = (value) => value == null ? '—' : value ? 'Sim' : 'Não';
const status = (value) => ({ ACTIVE: 'Ativo', PLANNED: 'Planejado', ENDED: 'Encerrado', INACTIVE: 'Inativo' }[value] || value || '—');
const fields = [
  ['registrationNumber', 'Matrícula'], ['apprenticeNumber', 'Número de aprendiz'], ['fullName', 'Nome'], ['cpf', 'CPF'], ['studentStatus', 'Status do aluno'],
  ['companyName', 'Empresa'], ['companyCorporateName', 'Razão social'], ['companyCnpj', 'CNPJ'], ['employmentStatus', 'Status do vínculo'],
  ['admissionDate', 'Data de admissão'], ['startDate', 'Início'], ['endDate', 'Saída'], ['role', 'Função'], ['workload', 'Carga horária'], ['hoursPerDay', 'Horas/dia'],
  ['salaryCents', 'Salário'], ['scholarshipCents', 'Bolsa'], ['transportationVoucher', 'Vale-transporte'], ['deductTransportationVoucher', 'Descontar VT'], ['transportationVoucherDiscountPercent', '% desconto VT'],
  ['mealAllowance', 'Vale-alimentação'], ['mealAllowanceCents', 'Valor VA'], ['bankName', 'Banco'], ['bankAgency', 'Agência'], ['bankAccount', 'Conta'],
  ['courseName', 'Curso'], ['courseDay', 'Dia do curso'], ['courseTime', 'Horário do curso'], ['vacationStartDate', 'Início das férias'], ['vacationDays', 'Dias de férias'], ['vacationBilled', 'Férias pendentes de cobrança'],
  ['terminationDate', 'Data de rescisão'], ['terminationReason', 'Rescisão / motivo'], ['notes', 'Observações'],
];
const defaultFields = ['apprenticeNumber', 'fullName', 'cpf', 'companyName', 'employmentStatus', 'startDate', 'role', 'workload', 'salaryCents', 'scholarshipCents'];
const initialFilters = () => ({
  search: '', companyId: '', status: '', studentStatus: '', admissionFrom: '', admissionTo: '', role: '', courseName: '', apprenticeNumber: '',
});
const value = (row, key) => {
  if (['salaryCents', 'scholarshipCents', 'mealAllowanceCents'].includes(key)) return money(row[key]);
  if (['admissionDate', 'startDate', 'endDate', 'vacationStartDate', 'terminationDate'].includes(key)) return dateBr(row[key]);
  if (['transportationVoucher', 'deductTransportationVoucher', 'mealAllowance', 'vacationBilled'].includes(key)) return yesNo(row[key]);
  if (key === 'employmentStatus' || key === 'studentStatus') return status(row[key]);
  if (key === 'transportationVoucherDiscountPercent') return `${Number(row[key] || 0).toLocaleString('pt-BR')}%`;
  return row[key] == null || row[key] === '' ? '—' : String(row[key]);
};
const distinct = (items) => Array.from(new Set(items.map((item) => String(item || '').trim()).filter(Boolean)))
  .sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));

function YoungApprenticeSecretariatReport() {
  const [companies, setCompanies] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(initialFilters());
  const [selected, setSelected] = useState(defaultFields);
  const [fieldSearch, setFieldSearch] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceGenerating, setAttendanceGenerating] = useState(false);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const attendanceCacheRef = useRef(null);
  const [attendanceError, setAttendanceError] = useState('');
  const [attendanceDay, setAttendanceDay] = useState('');
  const [attendancePeriod, setAttendancePeriod] = useState('');

  const load = async (next = filters) => {
    setLoading(true); setFeedback('');
    try {
      const [companyRows, reportRows, organizationResult] = await Promise.all([
        companies.length ? Promise.resolve(companies) : YoungApprenticeService.listCompanies({ includeInactive: true }),
        YoungApprenticeService.listApprenticeReport(next),
        organization ? Promise.resolve({ data: organization }) : api.get('/company').catch(() => ({ data: null })),
      ]);
      if (!companies.length) setCompanies(companyRows || []);
      if (!organization && organizationResult?.data) setOrganization(organizationResult.data);
      setRows(Array.isArray(reportRows) ? reportRows : []);
    } catch (error) { setFeedback(error?.response?.data?.error || 'Não foi possível carregar o relatório.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(initialFilters()); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleFields = useMemo(() => {
    const term = fieldSearch.trim().toLocaleLowerCase('pt-BR');
    return fields.filter(([, label]) => !term || label.toLocaleLowerCase('pt-BR').includes(term));
  }, [fieldSearch]);
  const selectedFields = selected.map((key) => fields.find(([fieldKey]) => fieldKey === key)).filter(Boolean);
  const toggle = (key) => setSelected((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);
  const moveSelected = (key, direction) => setSelected((current) => { const index = current.indexOf(key); const target = index + direction; if (index < 0 || target < 0 || target >= current.length) return current; const next = [...current]; [next[index], next[target]] = [next[target], next[index]]; return next; });
  const selectedCompany = companies.find((company) => String(company.id) === String(filters.companyId));
  const filterSummary = [
    filters.search.trim() ? `Busca: ${filters.search.trim()}` : '',
    selectedCompany ? `Empresa: ${selectedCompany.tradeName || selectedCompany.corporateName}` : 'Todas as empresas',
    filters.status ? `Vínculo: ${status(filters.status)}` : 'Todos os vínculos',
    filters.studentStatus ? `Aluno: ${status(filters.studentStatus)}` : '',
    filters.admissionFrom ? `Admissão a partir de ${dateBr(filters.admissionFrom)}` : '',
    filters.admissionTo ? `Admissão até ${dateBr(filters.admissionTo)}` : '',
    filters.role.trim() ? `Função: ${filters.role.trim()}` : '',
    filters.courseName.trim() ? `Curso: ${filters.courseName.trim()}` : '',
    filters.apprenticeNumber.trim() ? `Nº aprendiz: ${filters.apprenticeNumber.trim()}` : '',
  ].filter(Boolean).join(' • ');

  const attendanceDays = useMemo(() => distinct(attendanceRows.map((row) => row.courseDay)), [attendanceRows]);
  const attendancePeriods = useMemo(() => distinct(attendanceRows
    .filter((row) => !attendanceDay || String(row.courseDay || '').trim() === attendanceDay)
    .map((row) => row.classPeriod || row.courseTime)), [attendanceRows, attendanceDay]);

  const openAttendance = async () => {
    setAttendanceOpen(true);
    setAttendanceError('');
    if (attendanceCacheRef.current && Date.now() - attendanceCacheRef.current.at < 60000) {
      setAttendanceRows(attendanceCacheRef.current.rows);
      return;
    }
    setAttendanceLoading(true);
    try {
      const activeRows = await YoungApprenticeService.listAttendanceReport();
      const nextRows = Array.isArray(activeRows) ? activeRows : [];
      setAttendanceRows(nextRows);
      if (attendanceDay && !nextRows.some((row) => String(row.courseDay || '').trim() === attendanceDay)) {
        setAttendanceDay(''); setAttendancePeriod('');
      } else if (attendancePeriod && !nextRows.some((row) => String(row.courseDay || '').trim() === attendanceDay && String(row.classPeriod || row.courseTime || '').trim() === attendancePeriod)) {
        setAttendancePeriod('');
      }
      attendanceCacheRef.current = { rows: nextRows, at: Date.now() };
    } catch (error) {
      setAttendanceError(error?.response?.data?.error || 'Não foi possível carregar os aprendizes para a lista de presença.');
    } finally { setAttendanceLoading(false); }
  };

  const generateAttendance = async () => {
    if (!attendanceDay) return setAttendanceError('Selecione o dia de curso para gerar a lista de presença.');
    setAttendanceLoading(true); setAttendanceGenerating(true); setAttendanceError('');
    try {
      const presenceRows = await YoungApprenticeService.listAttendanceReport({
        courseDay: attendanceDay, period: attendancePeriod,
      });
      const cleanRows = (Array.isArray(presenceRows) ? presenceRows : []).filter((row) => row.fullName);
      await downloadYoungApprenticeAttendancePdf({
        organization,
        rows: cleanRows,
        courseDay: attendanceDay,
        period: attendancePeriod,
      });
      setFeedback(`Lista de presença gerada com ${new Set(cleanRows.map((row) => row.studentId)).size} aprendiz(es).`);
      setAttendanceOpen(false);
    } catch (error) {
      setAttendanceError(error?.response?.data?.error || error?.message || 'Não foi possível gerar a lista de presença.');
    } finally { setAttendanceLoading(false); setAttendanceGenerating(false); }
  };

  const downloadPdf = async () => {
    if (!selectedFields.length) return setFeedback('Selecione ao menos uma coluna.');
    setDownloading(true); setFeedback('');
    try {
      await downloadYoungApprenticeTablePdf({
        title: 'Relatório para Secretaria',
        subtitle: 'Gestão de Aprendiz',
        organization,
        rows,
        columns: selectedFields.map(([key, label]) => ({
          key,
          label,
          value: (row) => value(row, key),
          weight: ['fullName', 'companyName', 'companyCorporateName', 'role', 'courseName', 'notes'].includes(key) ? 1.45 : 1,
        })),
        filters: filterSummary,
        filename: `relatorio-mundo-do-trabalho-${new Date().toISOString().slice(0, 10)}.pdf`,
      });
      setFeedback('Relatório PDF baixado com sucesso.');
    } catch (error) {
      setFeedback(error?.message || 'Não foi possível gerar o PDF.');
    } finally { setDownloading(false); }
  };

  const excel = () => {
    if (!selectedFields.length) return setFeedback('Selecione ao menos uma coluna.');
    const csv = [selectedFields.map(([, label]) => label), ...rows.map((row) => selectedFields.map(([key]) => value(row, key)))]
      .map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.download = `mundo-do-trabalho-${new Date().toISOString().slice(0, 10)}.csv`; link.click(); URL.revokeObjectURL(url);
  };

  const clearFilters = async () => {
    const next = initialFilters();
    setFilters(next); setAdvancedOpen(false);
    await load(next);
  };

  return <div className="young-apprentice-page ya-font-montserrat ya-clean-page">
    <header className="ya-clean-header ya-clean-header--navy"><div><span className="ya-page-toolbar__icon"><FaBriefcase /></span><div className="ya-header-title-row"><h1>Relatório para Secretaria</h1><span className="ya-header-info" tabIndex="0" data-tooltip="Relatório profissional dos jovens com download direto em PDF no padrão Nash."><FaInfoCircle /></span></div></div><div className="ya-header-actions"><YoungApprenticeProgramReport organization={organization} /><button className="young-apprentice-button young-apprentice-button--secondary" onClick={openAttendance} disabled={attendanceLoading}><FaClipboardList /> Lista de presença</button><button className="young-apprentice-button young-apprentice-button--secondary" onClick={excel}><FaFileExcel /> Excel</button><button className="young-apprentice-button young-apprentice-button--primary" onClick={downloadPdf} disabled={downloading}><FaFilePdf /> {downloading ? 'Gerando...' : 'Baixar PDF'}</button></div></header>
    {feedback && <div className="young-apprentice-feedback">{feedback}</div>}

    {attendanceOpen && <YoungApprenticeAttendanceDialog
      loading={attendanceLoading} generating={attendanceGenerating} days={attendanceDays} periods={attendancePeriods}
      day={attendanceDay} period={attendancePeriod} count={attendanceRows.length}
      error={attendanceError} onRetry={openAttendance} onClose={() => setAttendanceOpen(false)}
      onDayChange={(day) => { setAttendanceDay(day); setAttendancePeriod(''); }}
      onPeriodChange={setAttendancePeriod} onGenerate={generateAttendance}
    />}

    <section className="young-apprentice-card ya-report-filters">
      <div className="ya-simple-filter__main ya-report-filter-main">
        <label className="young-apprentice-search ya-search-wide"><FaSearch /><input value={filters.search} onChange={(e) => setFilters((c) => ({ ...c, search: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') load(); }} placeholder="Jovem, CPF, empresa, função, curso ou nº aprendiz" /></label>
        <select value={filters.companyId} onChange={(e) => setFilters((c) => ({ ...c, companyId: e.target.value }))}><option value="">Todas as empresas</option>{companies.map((company) => <option key={company.id} value={company.id}>{company.tradeName || company.corporateName}</option>)}</select>
        <select value={filters.status} onChange={(e) => setFilters((c) => ({ ...c, status: e.target.value }))}><option value="">Todos os vínculos</option><option value="ACTIVE">Ativos</option><option value="PLANNED">Planejados</option><option value="ENDED">Encerrados</option></select>
        <button type="button" className="ya-compact-btn" onClick={() => setAdvancedOpen((current) => !current)}><FaFilter /> Mais filtros {advancedOpen ? <FaChevronUp /> : <FaChevronDown />}</button>
        <button type="button" className="ya-compact-btn ya-compact-btn--primary" onClick={() => load()}><FaSearch /> Buscar</button>
        <button type="button" className="ya-compact-btn" onClick={clearFilters}><FaUndo /> Limpar</button>
      </div>
      {advancedOpen && <div className="ya-simple-filter__advanced ya-report-advanced-filters">
        <label><span>Status do aluno</span><select value={filters.studentStatus} onChange={(e) => setFilters((c) => ({ ...c, studentStatus: e.target.value }))}><option value="">Todos</option><option value="ACTIVE">Ativos</option><option value="INACTIVE">Inativos</option></select></label>
        <label><span>Admissão de</span><input type="date" value={filters.admissionFrom} onChange={(e) => setFilters((c) => ({ ...c, admissionFrom: e.target.value }))} /></label>
        <label><span>Admissão até</span><input type="date" value={filters.admissionTo} onChange={(e) => setFilters((c) => ({ ...c, admissionTo: e.target.value }))} /></label>
        <label><span>Função</span><input value={filters.role} onChange={(e) => setFilters((c) => ({ ...c, role: e.target.value }))} placeholder="Ex.: Auxiliar" /></label>
        <label><span>Curso</span><input value={filters.courseName} onChange={(e) => setFilters((c) => ({ ...c, courseName: e.target.value }))} placeholder="Nome do curso" /></label>
        <label><span>Número de aprendiz</span><input value={filters.apprenticeNumber} onChange={(e) => setFilters((c) => ({ ...c, apprenticeNumber: e.target.value }))} placeholder="Número interno" /></label>
      </div>}
    </section>

    <section className="young-apprentice-card ya-report-columns"><div className="young-apprentice-card__title"><div><h2>Colunas do relatório</h2><small>{selected.length} de {fields.length} selecionadas</small></div><div className="ya-header-actions"><button className="ya-compact-btn" onClick={() => setSelected(fields.map(([key]) => key))}><FaCheckSquare /> Todas</button><button className="ya-compact-btn" onClick={() => setSelected([])}><FaSquare /> Limpar</button></div></div><div className="ya-report-order-editor"><div className="ya-report-order-editor__heading"><strong>Ordem das colunas</strong><span>A sequência abaixo será usada no PDF, no Excel e na prévia.</span></div><div className="ya-report-order-list">{selectedFields.map(([key,label],index)=><div className="ya-report-order-item" key={key}><span className="ya-report-order-index">{index+1}</span><strong>{label}</strong><div className="ya-report-order-actions"><button type="button" aria-label={'Mover '+label+' para cima'} disabled={index===0} onClick={()=>moveSelected(key,-1)}><FaArrowUp/></button><button type="button" aria-label={'Mover '+label+' para baixo'} disabled={index===selectedFields.length-1} onClick={()=>moveSelected(key,1)}><FaArrowDown/></button></div></div>)}</div></div><label className="young-apprentice-search"><FaSearch /><input value={fieldSearch} onChange={(e) => setFieldSearch(e.target.value)} placeholder="Procurar coluna" /></label><div className="ya-report-column-grid">{visibleFields.map(([key, label]) => <label key={key} className={selected.includes(key) ? 'is-selected' : ''}><input type="checkbox" checked={selected.includes(key)} onChange={() => toggle(key)} /><span>{label}</span></label>)}</div></section>

    <div className="ya-report-note">{loading ? 'Carregando os dados do relatório...' : `${rows.length.toLocaleString('pt-BR')} vínculo(s) encontrado(s). O PDF e o Excel incluem todos os registros filtrados.`}</div>
  </div>;
}


const reportTabs = [
  ['SECRETARIAT', 'Relatório para Secretaria'],
  ['VACATIONS', 'Relatório de Férias'],
  ['CONTRACT_ENDS', 'Relatório de Términos de Contratos'],
];
export default function YoungApprenticeReport() {
  const [tab, setTab] = useState('SECRETARIAT');
  const id = useId();
  const move = event => {
    const current = reportTabs.findIndex(([key]) => key === tab);
    const next = event.key === 'ArrowRight' ? (current + 1) % reportTabs.length
      : event.key === 'ArrowLeft' ? (current + reportTabs.length - 1) % reportTabs.length
      : event.key === 'Home' ? 0 : event.key === 'End' ? reportTabs.length - 1 : null;
    if (next === null) return;
    event.preventDefault();
    const key = reportTabs[next][0]; setTab(key); document.getElementById(`${id}-${key}`)?.focus();
  };
  return <div className="ya-report-workspace">
    <nav className="ya-report-tabs" role="tablist" aria-label="Relatórios da Gestão de Aprendiz" onKeyDown={move}>
      {reportTabs.map(([key, label]) => <button type="button" role="tab" key={key} id={`${id}-${key}`} aria-selected={tab === key} aria-controls={`${id}-panel`} tabIndex={tab === key ? 0 : -1} onClick={() => setTab(key)}>{label}</button>)}
    </nav>
    <section role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-${tab}`}>
      {tab === 'SECRETARIAT' ? <YoungApprenticeSecretariatReport /> : <YoungApprenticePeriodReport key={tab} reportType={tab} />}
    </section>
  </div>;
}

/* report-export-order-young-apprentice-v1 */
